import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Input, MessageBarBody, MessageBarTitle, Textarea } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// models
import {
  CreatePersonEmailCommand,
  UpdatePersonEmailCommand
} from '../../../models/Entities/Person/Email/EmailModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// functions
import * as ownFunctions from './PersonEmail.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const EmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PersonEmailFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [email, setEmail] = useState<string>('');
  const [comments, setComments] = useState<string>('');

  useEffect(() => {
    if (props.email) {
      setEmail(props.email.email || '');
      setComments(props.email.comments || '');
    } else {
      resetForm();
    }
  }, [props.email, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setEmail('');
    setComments('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const isEmailValid = EmailPattern.test(email.trim());

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!email.trim()) {
      errors.push(t('persons.emails.form.validation.required', { defaultValue: 'El correo es obligatorio' }));
    } else if (!isEmailValid) {
      errors.push(t('persons.emails.form.validation.format', { defaultValue: 'El correo no tiene un formato válido' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: CreatePersonEmailCommand = {
      personId: props.PersonId,
      email: email.trim(),
      comments: comments.trim() || null
    };

    if (props.email) {
      props.UpdateEmail({ ...payload, id: props.email.id } as UpdatePersonEmailCommand);
    } else {
      props.CreateEmail(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const isSaving = !!(props.isAdding || props.isUpdating);

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      eyebrow={props.personName || undefined}
      title={
        props.email
          ? t('persons.emails.form.editTitle', { defaultValue: 'Editar correo electrónico' })
          : t('persons.emails.form.addTitle', { defaultValue: 'Nuevo correo electrónico' })
      }
      onDismiss={handleCancel}
      preventDismiss={isSaving}
      testId="person-email-drawer"
      secondaryAction={{ label: t('persons.form.footer.cancel', { defaultValue: 'Cancelar' }) }}
      primaryAction={{
        label: t('persons.form.footer.save', { defaultValue: 'Guardar' }),
        onClick: handleSave,
        loading: isSaving
      }}
    >
      {submitted && validation && !validation.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}</MessageBarTitle>
            <ul>
              {validation.errors.map((error: string, index: number) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {submitted && (props.failAdding || props.failUpdating) && props.error && (
        <ErrorMessageComponent Error={props.error} />
      )}

      <GuideDrawerForm onSubmit={handleSave}>
        <GuideField
          label={t('persons.emails.form.email', { defaultValue: 'Correo electrónico' })}
          required
          error={
            submitted && !isEmailValid
              ? t('persons.emails.form.validation.format', {
                  defaultValue: 'El correo no tiene un formato válido'
                })
              : undefined
          }
        >
          <Input type="email" value={email} onChange={(_, data) => setEmail(data.value || '')} />
        </GuideField>

        <GuideField label={t('persons.emails.form.comments', { defaultValue: 'Comentarios' })}>
          <Textarea resize="vertical" value={comments} onChange={(_, data) => setComments(data.value || '')} />
        </GuideField>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonEmailFormComponent as any);
